import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import { LassieContext } from "../../context/LassieProvider";
import { defectAPI } from "../../api";
import UserActionsWplFileUpload from "../../components/user-action-wpl-file-upload/userActionWplFileUpload";
import WplInput from "../../components/wpl-input/WplInput";
import { toPrettyDateStr, toStandardWplTimestamp } from "../../prettyDate";
import { generateRandomId } from "../../consist/generateRandomId";
import closeIcon from '../../assets/img/cancel.png'
import closeIconW from "../../assets/img/close-w.png";
import LoaderAnimation from "../../assets/img/loader-spinner.gif"
import "./turbine-manager-popup.css";

export default function TurbineManagerPopup({
  showPopup,
  closePopup,
  className,
  removeTurbine
}) {
  const { createUserAction, organization, getInspectionTemplates, inspectionTemplates,
    selectedTurbineIds, handleResolveInspection, handleScheduleInspections, handleUploadInspectionData, handleRejectInspections, turbines, handleCreateDisposition, setMapPopUpLoading, feedback, setFeedback, mapPopUpLoading } = useContext(LassieContext);
  const me = useRef(null);
  const [downTarget, setDownTarget] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [isTemplateComment, setIsTemplateComment] = useState(false);
  const [userComment, setUserComment] = useState("");
  const [targetDate, setTargetDate] = useState(toPrettyDateStr(new Date()));
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showAddAction, setShowAddAction] = useState({ status: true, layout: "comment" });
  const [newDefect, setNewDefect] = useState({ defect_type: null, distance: null, layer: null, severity: null, blade: null, surface: null, repair_recommendation: null, report_defect_id: generateRandomId(), defect_id: null, is_internal: false });
  const defect = { blade: ["A", "B", "C"], surface: ["LE", "SS", "TE", "PS"], severity: ["1", "2", "3", "4", "5"] }
  const [inspectionOptions, setInspectionOptions] = useState({ inspection_type: null, inspection_method: null })

  const datePickerRef = useRef();
  const openDatePicker = useCallback(() => {
    if (!datePickerRef.current) return;
    datePickerRef.current.showPicker();
  }, [datePickerRef]);

  useEffect(() => {
    if (!organization || !organization.id) return;
    getInspectionTemplates(organization.id)
  }, [organization])

  const [templateIndex, setTemplateIndex] = useState(0);
  const currentTemplate =
    inspectionTemplates.length > 0
      ? inspectionTemplates[templateIndex]
      : { title: "No templates to show", comment: "Comment templates can be set up in the organization settings." };

  const uploadedFile = useCallback((filename, data) => {
    setUploadedDocuments(prev => ({ ...prev, [filename]: data }));
  }, []);

  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => {
        setFeedback({ type: "", message: "" });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const deletedFile = useCallback((filename) => {
    setUploadedDocuments(prev => {
      const copy = { ...prev };
      delete copy[filename];
      return copy;
    });
  }, []);

  const submitUserAction = (action) => {
    handleCreateUserAction({
      turbine_ids: selectedTurbineIds.map(obj => obj.id),
      action: action,
      target_date: targetDate,
      user_comment: userComment,
      file_names: Object.keys(uploadedDocuments),
      file_datas: Object.keys(uploadedDocuments).map(
        (k) => uploadedDocuments[k]
      ),
    });
  };

  const handleCreateUserAction = async ({
    action,
  }) => {
    setMapPopUpLoading(true);

    try {
      await createUserAction({
        turbine_ids: selectedTurbineIds.map(obj => obj.id),
        action: action,
        target_date: targetDate,
        user_comment: userComment,
        file_names: Object.keys(uploadedDocuments),
        file_datas: Object.keys(uploadedDocuments).map(
          (k) => uploadedDocuments[k]
        ),
        due_in_hours: selectedTemplate?.due_in_hours || null,
        inspection_type: selectedTemplate?.inspection_type || null,
        inspection_method: selectedTemplate?.inspection_method || null,
      });

      setUserComment("");
    } catch (err) {
      console.error("Error creating user action:", err);
    } finally {
      setMapPopUpLoading(false);
    }
  };



  const createDefect = async ({ turbine_id, defect, image_source, defect_image_data, blade_image_data }) => {
    try {
      const result = await defectAPI.createDefect({ turbine_id, defect, image_source, defect_image_data, blade_image_data });
      return result;
    } catch (error) {
      console.error("Error creating defect:", error);
      throw error;
    }
  };

  const handleCreateDefect = async ({
    turbine_id,
    inspection_id,
    user_comment,
    image_source,
    defect_image_data,
    blade_image_data
  }) => {
    setMapPopUpLoading(true)
    const defect = newDefect;
    try {
      const createdDefectId = await createDefect({ turbine_id, defect, image_source, defect_image_data, blade_image_data });

      if (!createdDefectId) {
        throw new Error("Failed to create defect or defect id is missing");
      }

      setNewDefect((prev) => ({ ...prev, defect_id: createdDefectId }));

      await handleCreateDisposition({
        turbine_id: turbine_id,
        inspection_id: inspection_id,
        defect_id: createdDefectId,
        user_comment: user_comment
      });
    }
    catch (error) {
      console.error("Error creating defect and disposition:", error);
    } finally { }
    setMapPopUpLoading(false);
  };


  const handleCommentTypeSelector = () => {
    const switchingToTemplate = !isTemplateComment;
    setIsTemplateComment(switchingToTemplate);

    if (switchingToTemplate && inspectionTemplates.length > 0) {
      const newTemplate = inspectionTemplates[templateIndex];
      setUserComment(newTemplate.comment);
      setSelectedTemplate(newTemplate);
    } else {
      setUserComment("");
      setSelectedTemplate({});
    }
  };

  function handleNext() {
    if (!inspectionTemplates.length) return;

    const nextIndex = (templateIndex + 1) % inspectionTemplates.length;
    setTemplateIndex(nextIndex);
    const newTemplate = inspectionTemplates[nextIndex];
    setUserComment(newTemplate?.comment || '');
    setSelectedTemplate(newTemplate);
  }

  function handlePrev() {
    if (!inspectionTemplates.length) return;

    const prevIndex = (templateIndex - 1 + inspectionTemplates.length) % inspectionTemplates.length;
    setTemplateIndex(prevIndex);
    const newTemplate = inspectionTemplates[prevIndex];
    setUserComment(newTemplate?.comment || '');
    setSelectedTemplate(newTemplate);
  }

  const isRejected = () => {
    return selectedTurbineIds.some(selected => {
      const turbine = turbines.find(t => t.id === selected.id);
      if (!turbine || !turbine.inspection) return false;

      return (
        turbine.inspection.id === selected.inspection_id &&
        turbine.inspection.status === 'rejected'
      );
    });
  };

  const requiredFields = [
    "defect_type",
    "distance",
    "layer",
    "severity",
    "blade",
    "surface",
    "repair_recommendation"
  ];

  const isDefectIncomplete = requiredFields.some(field => {
    const value = newDefect[field];
    return value === null || value === "";
  });

  const getPopupGuideMessage = () => {
    if (selectedTurbineIds.length === 0) {
      return "Please select at least one turbine...";
    }
    if (selectedTurbineIds.some(t => t.inspection_id === null && showAddAction.layout !== 'schedule_inspection')) {
      return "Turbines with no inspection attached are shown in purple..."
    }
    if (selectedTurbineIds.some(t => t.inspection_id === null && showAddAction.layout === 'schedule_inspection')) {
      return "Turbines with no inspection attached will be updated..."
    }
    if (showAddAction.layout === 'inspection_report' && selectedTurbineIds.length !== 1) {
      return "Uploading inspection data is only allowed for a single turbine...";
    }
    if (showAddAction.layout === 'inspection_report' && Object.keys(uploadedDocuments).length === 0) {
      return "Upload attachment is required for uploading inspection data...";
    }
    if (showAddAction.layout === 'disposition' && selectedTurbineIds.length !== 1) {
      return "Defects can only be created for a single turbine.";
    }
    if (showAddAction.layout === 'disposition' && isDefectIncomplete) {
      return "Please complete all defect fields before creating a defect..."
    }
    if (showAddAction.layout === 'disposition' && !isDefectIncomplete && Object.keys(uploadedDocuments).length === 0) {
      return "Upload attachment is required for creating a defect...";
    }
    if (showAddAction.layout === 'schedule_inspection' && isRejected()) {
      return "Turbines with rejected inspections are shown in red...";
    }

    return '';
  };


  const guideMessage = getPopupGuideMessage();


  if (!showPopup) return <div></div>;

  return (
    <div
      ref={me}
      className="turbine-manager-popup"
      onMouseDown={(e) => setDownTarget(e.target)}
      onMouseUp={(e) => {
        if (e.target !== me.current) return;
        if (downTarget !== me.current) return;
        closePopup(false);
      }}
    >

      <div
        className={`turbine-manager-popup-wrapper ${className}`}
        onMouseDown={(e) => setDownTarget(e.target)}
      >
        <div className="close-btn-container">
          <img
            className="close-btn"
            onClick={(_) => closePopup(false)}
            src={closeIcon}
            alt="close popup"
          />

        </div>
        <div className="action-menu-btn-container">
          <button onClick={() => setShowAddAction({ status: true, layout: 'comment' })} className={`user-action-btn${showAddAction.layout === 'comment' ? ' user-action-btn-active' : ''}`}
          >
            Comment
          </button>
          <button
            disabled={
              selectedTurbineIds.length === 0 ||
              selectedTurbineIds.some(t => t.inspection_id === null) ||
              selectedTurbineIds.some(t => t.id === null)
            }
            onClick={() => setShowAddAction({ status: true, layout: "inspection_report" })} className={`user-action-btn${showAddAction.layout === 'inspection_report' ? ' user-action-btn-active' : ''}`}
          >
            Inspection report
          </button>
          <button
            disabled={
              selectedTurbineIds.length === 0 ||
              selectedTurbineIds.some(t => t.inspection_id === null) ||
              selectedTurbineIds.some(t => t.id === null)
            }
            onClick={() => { setShowAddAction({ status: true, layout: "disposition" }); setUserComment("") }} className={`user-action-btn${showAddAction.layout === 'disposition' ? ' user-action-btn-active' : ''}`}
          >
            Disposition
          </button>
          <button
            disabled={
              selectedTurbineIds.length === 0 ||
              selectedTurbineIds.some(t => t.id === null)
            }
            onClick={() => setShowAddAction({ status: true, layout: 'schedule_inspection' })} className={`user-action-btn${showAddAction.layout === 'schedule_inspection' ? ' user-action-btn-active' : ''}`}
          >
            Schedule inspection
          </button>
        </div>

        <div className="popup-guide-container">
          {!mapPopUpLoading && !feedback.message && guideMessage && (
            <p className="guide-message hint">{guideMessage}</p>
          )}

          {mapPopUpLoading && (
            <div className="guide-message-loading">
              <img src={LoaderAnimation} alt="Loading spinner" style={{ width: 40, height: 40 }} />
            </div>
          )}

          {!mapPopUpLoading && feedback.message && (
            <p className={`guide-message ${feedback.type === "error" ? "error" : "success"}`}>
              {feedback.message}
            </p>
          )}

          {!guideMessage && !mapPopUpLoading && !feedback.message && (
            <div className="guide-message-spacer"></div>
          )}
        </div>

        <div className="selected-turbines-popup">
          {turbines
            .filter((t) => selectedTurbineIds.some((sel) => sel.id === t.id))
            .map((t) => (
              <div
                key={t.id}
                className={`${t.inspection?.status === 'rejected' &&
                  showAddAction.layout === 'schedule_inspection' &&
                  !selectedTurbineIds.some(t => t.inspection_id === null)
                  ? 'turbine-name-badge-rejected'
                  : t.inspection === null
                    ? 'turbine-name-badge-no-inspection'
                    : 'turbine-name-badge'
                  }`}

                onClick={() => removeTurbine(t.id)}
              >
                <p className="turbine-name">{t.name}</p>
                <img
                  src={closeIconW}
                  className="delete-badge-icon"
                  alt="close icon"
                />
              </div>
            ))}
        </div>

        {showAddAction.status && (showAddAction.layout === 'comment' || showAddAction.layout === 'inspection_report' || showAddAction.layout === 'schedule_inspection') &&

          <div className="action-form-container">
            <div className="template-date-selector-header">

              <div className="cursor-pointer">
                <div
                  className="comment-type-selector"
                  onClick={() => handleCommentTypeSelector()}
                >
                  <div
                    className={`comment-slider ${isTemplateComment ? "right" : "left"}`}
                  />
                  <p
                    className={`comment-label ${!isTemplateComment ? "active" : ""}`}
                  >
                    Add a comment
                  </p>
                  <p
                    className={`comment-label ${isTemplateComment ? "active" : ""}`}
                  >
                    Use a template
                  </p>
                </div>
              </div>


              <div className="date-selection-wrapper">
                <WplInput
                  inputRef={datePickerRef}
                  onClick={openDatePicker}
                  className="performed-inspection-date"
                  type="date"
                  value={targetDate}
                  onChanged={(e) => setTargetDate(e)}
                />
              </div>
            </div>

            <div className="attachments-wrapper">
              <div >
                {isTemplateComment ? (
                  <div className="comment-area-wrapper">
                    <div className="template-selector">
                      <button className="arrow-button"
                        onClick={handlePrev}
                      >
                        ◀
                      </button>
                      <div className="template-title">{currentTemplate.title}</div>
                      <button className="arrow-button"
                        onClick={handleNext}
                      >
                        ▶
                      </button>
                    </div>

                    <textarea
                      className="comment-area"
                      value={currentTemplate.comment}
                      readOnly
                    />
                  </div>
                ) : (
                  <div className="comment-area-wrapper">
                    <textarea
                      className="comment-area"
                      placeholder="Add comment..."
                      value={userComment}
                      onChange={(e) => setUserComment(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {showAddAction.layout !== 'schedule_inspection' &&
                <div className="upload-wrapper">
                  <UserActionsWplFileUpload
                    text="Upload inspection-related documents"
                    asPopup={false}
                    displayFiles={Object.keys(uploadedDocuments).length > 0}
                    didSelectFile={uploadedFile}
                    fullWidth={true}
                    didDeleteFile={deletedFile}
                    files={uploadedDocuments}
                  />
                </div>
              }


              {showAddAction.layout === "schedule_inspection" && !isTemplateComment &&
                <div className="inspection-select-container">
                  <span className="input-wrapper">
                    <label>Inspection type</label>
                    <select
                      value={
                        isTemplateComment
                          ? currentTemplate.inspection_type ?? ""
                          : inspectionOptions.inspection_type ?? ""
                      }

                      onChange={(e) =>
                        setInspectionOptions((prev) => ({
                          ...prev,
                          inspection_type: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">Not selected</option>
                      <option value="internal">Internal</option>
                      <option value="external">External</option>
                    </select>
                  </span>
                  <span>
                    <label>Inspection method</label>
                    <select
                      value={
                        isTemplateComment
                          ? currentTemplate.inspection_method ?? ""
                          : inspectionOptions.inspection_method ?? ""
                      }

                      onChange={(e) =>
                        setInspectionOptions((prev) => ({
                          ...prev,
                          inspection_method: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">Not selected</option>
                      <option value="ground">Ground</option>
                      <option value="drone">Drone</option>
                    </select>
                  </span>
                </div>
              }
            </div>
          </div>
        }


        {showAddAction.status && showAddAction.layout === "disposition" &&
          <div>
            <div className="disposition-form">
              <div className="disposition-select-container">
                <span className="input-wrapper">
                  <label>Blade</label>
                  <select
                    value={newDefect.blade || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, blade: e.target.value }))}
                  >
                    <option value="" disabled>Select blade</option>
                    {defect.blade.map(blade =>
                      <option key={blade} value={blade}>{blade}</option>
                    )}
                  </select>
                </span>
                <span>
                  <label>Surface</label>
                  <select
                    value={newDefect.surface || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, surface: e.target.value }))}
                  >
                    <option value="" disabled>Select surface</option>
                    {defect.surface.map(surface =>
                      <option key={surface} value={surface}>{surface}</option>
                    )}
                  </select>
                </span>
                <span>
                  <label>Severity</label>
                  <select
                    value={newDefect.severity || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, severity: e.target.value }))}
                  >
                    <option value="" disabled>Select severity</option>
                    {defect.severity.map(severity =>
                      <option key={severity} value={severity}>{severity}</option>
                    )}
                  </select>
                </span>
              </div>

              <div className="disposition-input-container">
                <span>
                  <label>Defect Type</label>
                  <input
                    type="text"
                    value={newDefect.defect_type || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, defect_type: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Layer</label>
                  <input
                    type="text"
                    value={newDefect.layer || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, layer: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Vertical distance(m)</label>
                  <input
                    type="number"
                    value={newDefect.distance || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, distance: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Repair recommendation</label>
                  <input
                    type="text"
                    value={newDefect.repair_recommendation || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, repair_recommendation: e.target.value }))}
                  />
                </span>
              </div>
            </div>
            <div className="attachments-wrapper">
              <div className="comment-area-wrapper">
                <textarea
                  placeholder="Add comment..."
                  className="comment-area"
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                />
              </div>

              <UserActionsWplFileUpload
                text="Upload inspection-related documents"
                asPopup={false}
                displayFiles={Object.keys(uploadedDocuments).length > 0}
                didSelectFile={uploadedFile}
                fullWidth={true}
                didDeleteFile={deletedFile}
                files={uploadedDocuments}
              />
            </div>
          </div>
        }

        {showAddAction.status && showAddAction.layout === 'comment' &&
          <div className="btn-container">
            <button
              disabled={mapPopUpLoading || selectedTurbineIds.length === 0}
              onClick={() => {
                submitUserAction(
                  "marked_for_inspection",
                );
              }}
              className="user-action-btn">
              Add comment
            </button>

            <button
              disabled={mapPopUpLoading || selectedTurbineIds.length === 0}
              onClick={() => {
                submitUserAction(
                  "acceptable_risk",
                );
              }}
              className="user-action-btn">
              Accept risk
            </button>
          </div>
        }

        {showAddAction.status && showAddAction.layout === 'inspection_report' &&
          <div className="btn-container">
            <button
              disabled={
                mapPopUpLoading ||
                selectedTurbineIds.length === 0 ||
                selectedTurbineIds.some(t => t.inspection_id === null) ||
                selectedTurbineIds.some(t => t.id === null)
              }

              onClick={() => {
                const formattedDate = toStandardWplTimestamp(new Date(targetDate));
                handleResolveInspection({
                  turbine_ids: selectedTurbineIds.map(t => t.id),
                  inspection_ids: selectedTurbineIds.map(t => t.inspection_id),
                  inspection_date: formattedDate, user_comment: userComment
                })
              }}
              className="user-action-btn">
              Resolve inspection
            </button>
            <button
              disabled={
                mapPopUpLoading ||
                selectedTurbineIds.length !== 1 ||
                selectedTurbineIds[0].inspection_id === null ||
                Object.keys(uploadedDocuments).length === 0
              }
              onClick={() => {
                handleUploadInspectionData({
                  turbine_id: selectedTurbineIds[0]?.id,
                  inspection_id: selectedTurbineIds[0]?.inspection_id,
                  file_names: Object.keys(uploadedDocuments),
                  file_datas: Object.keys(uploadedDocuments).map(
                    (k) => uploadedDocuments[k]
                  ),
                  target_date: targetDate,
                  user_comment: userComment.trim() === "" ? null : userComment,
                });
              }}
              className="user-action-btn"
            >
              Upload inspection data
            </button>

          </div>
        }

        {showAddAction.status && showAddAction.layout === 'disposition' &&
          <div className="btn-container">
            <button
              disabled={
                mapPopUpLoading ||
                selectedTurbineIds.length !== 1 || selectedTurbineIds.some(t => t.inspection_id === null) ||
                Object.keys(uploadedDocuments).length === 0 ||
                isDefectIncomplete
              }
              onClick={() => {
                handleCreateDefect({
                  turbine_id: selectedTurbineIds[0]?.id,
                  inspection_id: selectedTurbineIds[0]?.inspection_id,
                  user_comment: userComment.trim() === "" ? null : userComment,
                  image_source: 'local',
                  defect_image_data: uploadedDocuments[Object.keys(uploadedDocuments)[0]],
                  blade_image_data: uploadedDocuments[Object.keys(uploadedDocuments)[0]],
                })
              }

              }
              className="user-action-btn">
              Create defect
            </button>
            <button
              disabled={
                mapPopUpLoading ||
                selectedTurbineIds.length === 0 ||
                selectedTurbineIds.some(t => t.inspection_id === null) ||
                selectedTurbineIds.some(t => t.id === null)
              }
              onClick={() => {
                const formattedDate = toStandardWplTimestamp(new Date(targetDate));
                handleResolveInspection({
                  turbine_ids: selectedTurbineIds.map(t => t.id),
                  inspection_ids: selectedTurbineIds.map(t => t.inspection_id),
                  inspection_date: formattedDate,
                  user_comment: userComment.trim() === "" ? null : userComment,
                })
              }}
              className="user-action-btn">
              Resolve inspection
            </button>
          </div>
        }

        {showAddAction.status && showAddAction.layout === 'schedule_inspection' &&
          <div className="btn-container">
            <button
              disabled={selectedTurbineIds.some(t => t.inspection_id === null) || isRejected()}
              onClick={() => {
                const formattedDate = toStandardWplTimestamp(new Date(targetDate));
                handleRejectInspections({
                  turbine_ids: selectedTurbineIds.map(t => t.id),
                  inspection_ids: selectedTurbineIds.map(t => t.inspection_id),
                  user_comment: userComment.trim() === "" ? null : userComment,
                  inspection_date: formattedDate,
                })
              }}
              className="user-action-btn">
              Reject inspection
            </button>
            <button
              disabled={
                mapPopUpLoading ||
                selectedTurbineIds.length === 0 ||
                selectedTurbineIds.some(t => t.id === null)
              }
              onClick={() => {
                const formattedDate = new Date(targetDate).toISOString().split('T')[0];
                const inspection_type = isTemplateComment
                  ? selectedTemplate?.inspection_type ?? null
                  : inspectionOptions.inspection_type;

                const inspection_method = isTemplateComment
                  ? selectedTemplate?.inspection_method ?? null
                  : inspectionOptions.inspection_method;

                handleScheduleInspections({
                  turbine_ids: selectedTurbineIds.map(t => t.id),
                  inspection_date: formattedDate,
                  user_comment: userComment.trim() === "" ? null : userComment,
                  inspection_type,
                  inspection_method,
                });
              }}
              className="user-action-btn">
              Schedule inspection
            </button>
          </div>
        }
      </div>
    </div>
  );
}
